/*
 * Lista de filtros para la lista de importaciones.
 *
 * Contiene los componentes de buscador general, fecha de llegada, disponibilidad
 * fecha de pedido, fecha de entrega estimada (ETD) y fecha de llegada estimada (ETA).
 *
 * Created on Mon Oct 30 2023 by José Luis Chávez Ayala.
 *
 * Copyright (c) 2023 Turn My App.
 */
<template>
  <div>

    <b-form-row>

      <!-- #region::Columns for main filters -->
      <b-col md="10">
        <b-form-row>

          <!-- #region::Searcher -->
          <b-col
            cols="2"
            class="mb-1"
          >
            <BasicSearcher
              ref="basic-searcher"
              placeholder="Buscar"
              :callback="handleSearch"
            />
          </b-col>
          <!-- #endregion::Searcher -->

          <!-- #region::Arrival date filter -->
          <b-col
            v-if="canShowFilter(ARRIVAL_DATE_FIELD_NAME)"
            cols="2"
          >
            <flat-pickr
              id="arrival"
              v-model="selectedArrivalDate"
              class="form-control"
              style="background-color: white;"
              :config="config"
              placeholder="Fecha de llegada"
              name="arrival"
              @on-close="onCloseDateFilter"
            />

            <b-button
              v-if="showClearArrivalDateFilterButton"
              variant="primary"
              class="mt-1"
              style="padding: 0.5rem 1rem;"
              @click="onClearDateFilter('arrival')"
            >
              Limpiar
            </b-button>
          </b-col>
          <!-- #endregion::Arrival date filter -->

          <!-- #region::Payment status filter -->
          <b-col
            v-if="canShowFilter(AVAILABILITY_FIELD_NAME)"
            cols="2"
          >
            <v-select
              v-model="selectedAvailability"
              label="text"
              class="style-chooser"
              placeholder="Disponibilidad"
              :options="availabilities"
            />
          </b-col>
          <!-- #endregion::Payment status filter -->

          <!-- #region::Order date filter -->
          <b-col
            v-if="canShowFilter(ORDER_DATE_FIELD_NAME)"
            cols="2"
          >
            <flat-pickr
              id="order"
              v-model="selectedOrderDate"
              class="form-control"
              style="background-color: white;"
              :config="config"
              placeholder="Fecha de pedido"
              name="order"
              @on-close="onCloseDateFilter"
            />

            <b-button
              v-if="showClearOrderDateFilterButton"
              variant="primary"
              class="mt-1"
              style="padding: 0.5rem 1rem;"
              @click="onClearDateFilter('order')"
            >
              Limpiar
            </b-button>
          </b-col>
          <!-- #endregion::Order date filter -->

          <!-- #region::ETD date filter -->
          <b-col
            v-if="canShowFilter(ETD_DATE_FIELD_NAME)"
            cols="2"
          >
            <flat-pickr
              id="etd"
              v-model="selectedETDDate"
              class="form-control"
              style="background-color: white;"
              :config="config"
              placeholder="ETD"
              name="etd"
              @on-close="onCloseDateFilter"
            />

            <b-button
              v-if="showClearETDDateFilterButton"
              variant="primary"
              class="mt-1"
              style="padding: 0.5rem 1rem;"
              @click="onClearDateFilter('etd')"
            >
              Limpiar
            </b-button>
          </b-col>
          <!-- #endregion::ETD date filter -->

          <!-- #region::ETA date filter -->
          <b-col
            v-if="canShowFilter(ETA_DATE_FIELD_NAME)"
            cols="2"
          >
            <flat-pickr
              id="eta"
              v-model="selectedETADate"
              class="form-control"
              style="background-color: white;"
              :config="config"
              placeholder="ETA"
              name="eta"
              @on-close="onCloseDateFilter"
            />

            <b-button
              v-if="showClearETADateFilterButton"
              variant="primary"
              class="mt-1"
              style="padding: 0.5rem 1rem;"
              @click="onClearDateFilter('eta')"
            >
              Limpiar
            </b-button>
          </b-col>
          <!-- #endregion::ETA date filter -->

        </b-form-row>
      </b-col>
      <!-- #endregion::Columns for main filters -->

      <!-- #region::Column for table columns showing filter -->
      <b-col md="2">
        <b-form-row>

          <!-- #region::Columns filter -->
          <b-col
            cols="12"
            md="auto"
            class="ml-auto"
          >
            <b-dropdown
              variant="outline-secondary"
              class="style-chooser"
              dropright
            >

              <template slot="button-content">
                <feather-icon
                  icon="ColumnsIcon"
                  class="mr-50"
                />
              </template>

              <b-dropdown-form>

                <!-- #region::Filter columns -->
                <BasicSearcher
                  ref="filter-searcher"
                  :callback="handleSearchColumns"
                  placeholder="Buscar"
                />
                <!-- #endregion::Filter columns -->

                <b-form-checkbox
                  v-for="(column, index) in filteredColumsBySearchTerm"
                  :key="index"
                  class="mt-50"
                  :checked="column.active"
                  @change="onColumnOptionChange(column)"
                >
                  {{ column.text }}
                </b-form-checkbox>
              </b-dropdown-form>
            </b-dropdown>
          </b-col>
          <!-- #endregion::Columns filter -->

          <!-- #region::Download pdf button -->
          <b-col
            cols="12"
            md="auto"
          >
            <b-button
              variant="flat-success"
              class="csv-btn float-right"
              :disabled="sendingEmail"
              @click="onSendingEamil"
            >
              <b-spinner
                v-if="sendingEmail"
                small
              />
              <feather-icon
                v-if="!sendingEmail"
                icon="MailIcon"
              />
            </b-button>
          </b-col>
          <!-- #endregion::Download pdf button -->

        </b-form-row>
      </b-col>
      <!-- #endregion::Column for table columns showing filter -->

    </b-form-row>

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  BCol, BFormRow, BDropdown, BDropdownForm, BButton, BSpinner, BFormCheckbox,
} from 'bootstrap-vue'

import vSelect from 'vue-select'
import BasicSearcher from '@/components/forms/BasicSearcher.vue'

import flatPickr from 'vue-flatpickr-component'
// eslint-disable-next-line no-unused-vars, import/no-extraneous-dependencies, import/extensions
import { Spanish } from 'flatpickr/dist/l10n/es.js'
// eslint-disable-next-line import/no-extraneous-dependencies
import 'flatpickr/dist/themes/material_blue.css'
// eslint-disable-next-line import/no-extraneous-dependencies
import confirmDatePlugin from 'flatpickr/dist/plugins/confirmDate/confirmDate'

import getError from '@/helpers/ErrorsHandler'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const ETD_DATE_FIELD_NAME = 'Etd'
const ETA_DATE_FIELD_NAME = 'Eta'
const ORDER_DATE_FIELD_NAME = 'OrderDate'
const ARRIVAL_DATE_FIELD_NAME = 'ArriveDate'
const AVAILABILITY_FIELD_NAME = 'Availability'

export default {
  components: {
    BCol,
    vSelect,
    BButton,
    BSpinner,
    BFormRow,
    BDropdown,
    flatPickr,
    BFormCheckbox,
    BDropdownForm,
    BasicSearcher,
  },
  props: {
    /**
     * Titulo que se muestra en la pestaña
     */
    title: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      selectedAvailability: null,

      /**
       * Valores para filtros de fechas
       */
      selectedETDDate: null,
      selectedETADate: null,
      selectedOrderDate: null,
      selectedArrivalDate: null,

      showClearETDDateFilterButton: false,
      showClearETADateFilterButton: false,
      showClearOrderDateFilterButton: false,
      showClearArrivalDateFilterButton: false,

      sendingEmail: false,
      columnsSearchTerm: '',

      /**
       * Config params for date range picker
       */
      config: {
        mode: 'range',
        locale: Spanish,
        ignoredFocusElements: [window.document.body],
        plugins: [confirmDatePlugin({
          confirmIcon: "<span style='cursor: pointer;'>Aceptar <i class='fa fa-check my-1'></i></span>",
          confirmText: '',
          showAlways: false,
          theme: 'light', // or "dark"
        })],
      },

      ETD_DATE_FIELD_NAME,
      ETA_DATE_FIELD_NAME,
      ORDER_DATE_FIELD_NAME,
      ARRIVAL_DATE_FIELD_NAME,
      AVAILABILITY_FIELD_NAME,
    }
  },
  computed: {
    ...mapGetters({
      getListsParams: 'imports/getListsParams',
      getAvailabilities: 'pieces/getAvailabilities',
      /**
       * Filtros globales
       */
      getFilteredSearchTerm: 'filters/getFilteredSearchTerm',
      getFilteredAvailability: 'filters/getFilteredAvailability',
      getFilteredETDDateRange: 'filters/getFilteredETDDateRange',
      getFilteredETADateRange: 'filters/getFilteredETADateRange',
      getFilteredOrderDateRange: 'filters/getFilteredOrderDateRange',
      getFilteredArrivalDateRange: 'filters/getFilteredArrivalDateRange',
    }),
    /**
     * Disponibilidades de las piezas
     */
    availabilities: {
      get() { return this.getAvailabilities },
    },
    /**
     * Indica el término de búsqueda seleccionado (paginado global)
     */
    filteredSearchTerm: {
      get() { return this.getFilteredSearchTerm },
    },
    /**
     * Disponibilidad seleccionada (filtro en store)
     */
    filteredAvailability: {
      get() { return this.getFilteredAvailability },
      set(value) { this.setFilteredAvailability(value) },
    },
    /**
     * Fecha ETD seleccionado (filtro en store)
     */
    filteredETDDateRange: {
      get() { return this.getFilteredETDDateRange },
      set(value) { this.setFilteredETDDateRange(value) },
    },
    /**
     * Fecha ETA seleccionado (filtro en store)
     */
    filteredETADateRange: {
      get() { return this.getFilteredETADateRange },
      set(value) { this.setFilteredETADateRange(value) },
    },
    /**
     * Fecha de pedido seleccionado (filtro en store)
     */
    filteredOrderDateRange: {
      get() { return this.getFilteredOrderDateRange },
      set(value) { this.setFilteredOrderDateRange(value) },
    },
    /**
     * Fecha de llegada seleccionado (filtro en store)
     */
    filteredArrivalDateRange: {
      get() { return this.getFilteredArrivalDateRange },
      set(value) { this.setFilteredArrivalDateRange(value) },
    },
    /**
     * Columnas de la pestala seleccionada, valida si se deben mostrar las columna de proveedor
     */
    filteredColumns: {
      get() {
        const { selectedColumns } = this.getListsParams.find(p => p.title === this.title)
        return selectedColumns
      },
    },
    /**
     * Columnas filtradas por término de búsqueda del filtro de columnas
     */
    filteredColumsBySearchTerm: {
      get() {
        return this.filteredColumns.filter(c => c.text.toLowerCase().includes(this.columnsSearchTerm.toLowerCase()))
      },
    },
  },

  /**
  * Carga información del estado
  * @summary Si existe información relacionada con los filtros cargados en el store,
  * se cargan en el componente
  */
  mounted() {
    // Carga el término de búsqueda (filtro en store)
    if (this.filteredSearchTerm) {
      this.$refs['basic-searcher'].loadSearchTerm(this.filteredSearchTerm)
    }

    // Carga la disponibilidad seleccionada
    this.selectedAvailability = this.filteredAvailability ? this.filteredAvailability : null
    this.$watch('selectedAvailability', availability => {
      this.$emit('on:filter', { type: 'availability', value: availability !== null ? availability.text : '' })
    })

    // Carga el estado de rango de fechas ETD (filtro en store)
    if (this.filteredETDDateRange) {
      this.selectedETDDate = `${this.filteredETDDateRange.from} a ${this.filteredETDDateRange.to}`
      this.showClearETDDateFilterButton = true
    } else {
      this.selectedETDDate = null
      this.showClearETDDateFilterButton = false
    }

    // Carga el estado de rango de fechas ETA (filtro en store)
    if (this.filteredETADateRange) {
      this.selectedETADate = `${this.filteredETADateRange.from} a ${this.filteredETADateRange.to}`
      this.showClearETADateFilterButton = true
    } else {
      this.selectedETADate = null
      this.showClearETADateFilterButton = false
    }

    // Carga el estado de rango de fechas de orden (filtro en store)
    if (this.filteredOrderDateRange) {
      this.selectedOrderDate = `${this.filteredOrderDateRange.from} a ${this.filteredOrderDateRange.to}`
      this.showClearOrderDateFilterButton = true
    } else {
      this.selectedOrderDate = null
      this.showClearOrderDateFilterButton = false
    }

    // Carga el estado de rango de fechas de llegada (filtro en store)
    if (this.filteredArrivalDateRange) {
      this.selectedArrivalDate = `${this.filteredArrivalDateRange.from} a ${this.filteredArrivalDateRange.to}`
      this.showClearArrivalDateFilterButton = true
    } else {
      this.selectedArrivalDate = null
      this.showClearArrivalDateFilterButton = false
    }
  },

  methods: {
    ...mapActions({
      sendCSVFileEmail: 'imports/sendCSVFileEmail',
      /**
       * Acciones para filtros globales
       */
      setFilteredAvailability: 'filters/setFilteredAvailability',
      setFilteredETDDateRange: 'filters/setFilteredETDDateRange',
      setFilteredETADateRange: 'filters/setFilteredETADateRange',
      setFilteredOrderDateRange: 'filters/setFilteredOrderDateRange',
      setFilteredArrivalDateRange: 'filters/setFilteredArrivalDateRange',
    }),

    /**
    * Evento del buscador básico
    * @summary Emite un evento con el término de búsqueda y limpia los campos de selección
    * @param {String} searchTerm - Término de búsqueda
    */
    handleSearch(searchTerm) {
      this.selectedAvailability = null
      this.filteredAvailability = null

      this.onClearDateFilter('etd', false)
      this.filteredETDDateRange = null

      this.onClearDateFilter('eta', false)
      this.filteredETADateRange = null

      this.onClearDateFilter('order', false)
      this.filteredOrderDateRange = null

      this.onClearDateFilter('arrival', false)
      this.filteredArrivalDateRange = null

      this.$emit('on:filter', { type: 'search', value: searchTerm })
    },

    /**
    * Evento del buscador de filtro de columnas
    * @summary Atiende el evento de búsqueda del filtro de columnas
    * @param {String} searchTerm - Término de búsqueda
    */
    handleSearchColumns(searchTerm) {
      this.columnsSearchTerm = searchTerm
    },

    /**
    * Selección de columnas
    * @summary Emite un evento con la columna seleccionada y su estado (activa o inactiva)
    * @param {Object} column - Columna seleccionada
    */
    onColumnOptionChange(column) {
      const baseColumn = column
      baseColumn.active = !baseColumn.active
      this.$emit('on:column-option-change', baseColumn)

      if (baseColumn.value === ETD_DATE_FIELD_NAME && this.selectedETDDate) {
        this.onClearDateFilter('etd')
      }

      if (baseColumn.value === ETA_DATE_FIELD_NAME && this.selectedETADate) {
        this.onClearDateFilter('eta')
      }

      if (baseColumn.value === ORDER_DATE_FIELD_NAME && this.selectedOrderDate) {
        this.onClearDateFilter('order')
      }

      if (baseColumn.value === ARRIVAL_DATE_FIELD_NAME && this.selectedArrivalDate) {
        this.onClearDateFilter('arrival')
      }

      if (baseColumn.value === AVAILABILITY_FIELD_NAME && this.selectedAvailability) {
        this.selectedAvailability = null
      }
    },

    /**
     * Descarga el archivo PDF
     */
    async onSendingEamil() {
      try {
        this.sendingEmail = true
        await this.sendCSVFileEmail({
          url: 'download-import-numbers',
          loadParams: {
            preOwned: this.title === 'new' ? 0 : 1,
            searchTerm: this.filteredSearchTerm,
            availabilityTerm: this.filteredAvailability,
            etdDateStartTerm: this.filteredETDDateRange?.from,
            etdDateEndTerm: this.filteredETDDateRange?.to,
            etaDateStartTerm: this.filteredETADateRange?.from,
            etaDateEndTerm: this.filteredETADateRange?.to,
            orderDateStartTerm: this.filteredOrderDateRange?.from,
            orderDateEndTerm: this.filteredOrderDateRange?.to,
            arrivalDateStartTerm: this.filteredArrivalDateRange?.from,
            arrivalDateEndTerm: this.filteredArrivalDateRange?.to,
            /**
             * Columns list
             */
            activedColumnsTerm: this.filteredColumns.filter(c => c.active),
          },
        })

        this.showToast(
          'Correo enviado',
          'El correo electrónico ha sido enviado correctamente.',
          'success',
        )
      } catch (error) {
        this.showToast('Error de validación', getError(error), 'danger')
      } finally {
        this.sendingEmail = false
      }
    },

    /**
    * Evento de selección de fecha
    * @summary Atiendo al evento de selección de fecha del componente flatpickr
    * @param {String} date - Cadena de texto con las fechas seleccionadas
    */
    onCloseDateFilter(_, date, instance) {
      const from = date?.split('a')[0]?.trim()
      const to = date?.split('a')[1]?.trim()

      let dateRange = null

      if (from && to) {
        dateRange = { from, to }
      } else if (from && !to) {
        dateRange = { from, to: from }
      }

      if (dateRange) {
        switch (instance.element.name) {
          case 'etd':
            this.showClearETDDateFilterButton = true
            this.$emit('on:filter', { type: 'etdDate', value: dateRange !== null ? dateRange : '' })
            break
          case 'eta':
            this.showClearETADateFilterButton = true
            this.$emit('on:filter', { type: 'etaDate', value: dateRange !== null ? dateRange : '' })
            break
          case 'order':
            this.showClearOrderDateFilterButton = true
            this.$emit('on:filter', { type: 'orderDate', value: dateRange !== null ? dateRange : '' })
            break
          default:
            this.showClearArrivalDateFilterButton = true
            this.$emit('on:filter', { type: 'arrivalDate', value: dateRange !== null ? dateRange : '' })
            break
        }
      }
    },

    /**
    * Limpieza del campo de rangos de fecha
    * @summary Atiende al evento de limpieza del campo de rangos de fecha
    */
    onClearDateFilter(type, emitEvent = true) {
      switch (type) {
        case 'etd':
          this.selectedETDDate = null
          this.showClearETDDateFilterButton = false
          if (emitEvent) {
            this.$emit('on:filter', { type: 'etdDate', value: '' })
          }
          break
        case 'eta':
          this.selectedETADate = null
          this.showClearETADateFilterButton = false
          if (emitEvent) {
            this.$emit('on:filter', { type: 'etaDate', value: '' })
          }
          break
        case 'order':
          this.selectedOrderDate = null
          this.showClearOrderDateFilterButton = false
          if (emitEvent) {
            this.$emit('on:filter', { type: 'orderDate', value: '' })
          }
          break
        default:
          this.selectedArrivalDate = null
          this.showClearArrivalDateFilterButton = false
          if (emitEvent) {
            this.$emit('on:filter', { type: 'arrivalDate', value: '' })
          }
          break
      }
    },

    /**
     * Indica si se debe mostrar el filtro de columnas
     * @param {String} value - Valor de la columna
     */
    canShowFilter(value) {
      const checkedColumns = this.filteredColumns.filter(c => c.active && c.value === value)
      return checkedColumns.length > 0
    },

    /**
    * Muestra un mensaje de tipo toast
    * @param {string} title - Título del mensaje
    * @param {string} text - Contenido del mensaje
    * @param {string} variant - Tipo del mensaje (warning, success, danger)
    */
    showToast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, text, variant, icon: 'BellIcon',
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.style-chooser {
  background: #ffffff;
}

.col-padding {
  padding: 0 0.5rem 0 0.5rem;
}

.btn-outline-secondary {
  border: 1px solid !important;
  border-color: #c9c9c9 !important;
}

.dropdown-menu {
  min-width: 16rem !important;
}

.vs__selected button {
  display: none
}
</style>
